import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

const schema = yup.object({
  trackingNumber: yup.number().required("Tracking number is required"),
});

type FormData = {
  trackingNumber: number;
};

type Props = {
  onSubmit: (data: FormData) => void;
  showImageEnabled: boolean;
  onShowImageClick: () => void;
  serialNumberLabel?: string;
};

export type ModuleSearchFormRef = {
  reset: () => void;
};

const ModuleSearchForm = forwardRef<ModuleSearchFormRef, Props>(
  ({ onSubmit, showImageEnabled, onShowImageClick, serialNumberLabel }, ref) => {
    const {
      handleSubmit,
      setValue,
      reset,
      formState: { errors, isSubmitting },
      watch,
    } = useForm<FormData>({
      resolver: yupResolver(schema),
      defaultValues: { trackingNumber: undefined },
    });

    useImperativeHandle(ref, () => ({
      reset: () => reset(),
    }));
    const trackingValue = watch("trackingNumber");

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      let buffer = "";
      let timeout: NodeJS.Timeout;

      const handleKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;

        // Ignora se o foco estiver no input de tracking
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          document.activeElement === inputRef.current
        ) {
          return;
        }

        // Apenas aceita teclas de 1 caractere
        if (e.key.length === 1 || e.key === " ") {
          buffer += e.key;

          clearTimeout(timeout);
          timeout = setTimeout(() => {
            const value = buffer.trim();
            buffer = "";

            if (value) {
              if (inputRef.current) {
                inputRef.current.value = value;
              }

              setValue("trackingNumber", Number(value), {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });

              handleSubmit(onSubmit)();
            }
          }, 150); // timeout de silêncio (scanner rápido)
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleSubmit, onSubmit, setValue]);

    return (
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        alignItems="center"
        gap={2}
        sx={{ mb: 2 }}
      >
        <TextField
          inputRef={inputRef}
          label={serialNumberLabel || "Enter Tracking Number"}
          variant="outlined"
          size="small"
          value={trackingValue ?? ""}
          onChange={(e) =>
            setValue("trackingNumber", Number(e.target.value), {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
          error={!!errors.trackingNumber}
          helperText={errors.trackingNumber?.message}
        />

        <Button type="submit" variant="outlined" disabled={isSubmitting}>
          {isSubmitting ? "SEARCHING..." : "SEARCH"}
        </Button>

        <Button
          variant="outlined"
          disabled={!showImageEnabled}
          onClick={onShowImageClick}
        >
          SHOW EL-IMAGE
        </Button>
      </Box>
    );
  }
);

export default ModuleSearchForm;
